// LAP-550 — Card Immobilier : header + items immo formatés (image + meta + rendement)
// Améliorations : recherche personnalisée par profil utilisateur + bouton paramètres
import { Box, Button, Tooltip, CircularProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import AddOpportunityAction from '../../_shared/AddOpportunityAction'
import CardHeader from '../../_shared/CardHeader'
import Delta from '../../_shared/Delta'
import RealEstateSearchProfileForm from './RealEstateSearchProfileForm'
import { invTheme } from '../../_shared/theme'
import { makeGetRequest } from 'utils/api'

export default function RealEstateCard({ result = [] }) {
  const { t } = useTranslation('layout')
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(false)
  // LAP — le profil de recherche existant de l'utilisateur (s'il y en a un).
  // Avant ce correctif, la popup des paramètres n'avait aucun moyen de savoir
  // qu'un profil existait déjà : elle ne recevait jamais `profileId` ni
  // `initialValues`, donc à chaque réouverture elle repartait d'un formulaire
  // de CRÉATION vide, donnant l'impression que les critères précédemment
  // enregistrés n'étaient jamais conservés.
  const [activeProfile, setActiveProfile] = useState(null)

  // Charger les offres personnalisées + le profil de recherche existant
  useEffect(() => {
    fetchPersonalizedMatches()
    fetchActiveProfile()
  }, [])

  const fetchPersonalizedMatches = async () => {
    setLoading(true)
    try {
      const { data, status } = await makeGetRequest(
        '/api/investment/real-estate-matches/',
        {},
        true
      )
      if (status === 200 && data.results) {
        // Extraire les opportunités des matchs
        const opportunities = data.results.map((match) => match.opportunity)
        setMatches(opportunities)
      }
    } catch (error) {
      console.error('[RealEstateCard] Erreur chargement matchs :', error)
    }
    setLoading(false)
  }

  const fetchActiveProfile = async () => {
    try {
      const { data, status } = await makeGetRequest(
        '/api/investment/real-estate-search-profiles/',
        {},
        true
      )
      // Un seul profil éditable depuis cette card (le plus récent — le
      // modèle est ordonné par `-created_at`) : si l'utilisateur veut en
      // gérer plusieurs un jour, il faudra un vrai sélecteur, mais ça n'est
      // pas le cas aujourd'hui (une seule icône réglages dans le header).
      if (status === 200 && data?.results?.length > 0) {
        setActiveProfile(data.results[0])
      }
    } catch (error) {
      console.error('[RealEstateCard] Erreur chargement profil de recherche :', error)
    }
  }

  const handleProfileCreated = () => {
    // Recharger les offres + le profil après création/édition
    // (le popup se ferme déjà lui-même via son propre handleClose interne)
    fetchPersonalizedMatches()
    fetchActiveProfile()
  }

  // Décider quelle liste afficher
  const displayResult = matches.length > 0 ? matches : result
  const hasPersonalProfiles = matches.length > 0

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box flex={1}>
          <CardHeader
            icon="/images/Immobilier.png"
            title={t('investments.markeet.realEstate.title')}
            subtitle={t('investments.markeet.realEstate.subtitle')}
          />
        </Box>
        <Tooltip title={t('investments.markeet.realEstate.settings')}>
          <span>
            <RealEstateSearchProfileForm
              trigger={
                <Button size="small" sx={{ minWidth: 40, p: 0 }}>
                  <SettingsOutlinedIcon fontSize="small" />
                </Button>
              }
              profileId={activeProfile?.id}
              initialValues={
                activeProfile
                  ? {
                      label: activeProfile.label,
                      country: activeProfile.country,
                      localisation: activeProfile.localisation,
                      rayon_km: activeProfile.rayon_km,
                      prix_min: activeProfile.prix_min,
                      prix_max: activeProfile.prix_max,
                      type_bien: activeProfile.type_bien || '',
                      surface_min: activeProfile.surface_min,
                      pieces_min: activeProfile.pieces_min,
                      rendement_min: activeProfile.rendement_min,
                      actif: activeProfile.actif,
                    }
                  : undefined
              }
              onCreated={handleProfileCreated}
              onError={() => console.error('[RealEstateCard] Erreur formulaire')}
            />
          </span>
        </Tooltip>
      </Box>
      <Box mt={2} height="100%" display="flex" flexDirection="column" gap={1.25} sx={{ overflow: 'auto' }}>
        {loading && <CircularProgress size={24} />}
        {!loading && displayResult.length === 0 && !hasPersonalProfiles && (
          <Box sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 2 }}>
            <p>{t('investments.markeet.realEstate.noOffersGlobal')}</p>
          </Box>
        )}
        {!loading && displayResult.length === 0 && hasPersonalProfiles && (
          <Box sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 2 }}>
            <p>{t('investments.markeet.realEstate.noOffersMatched')}</p>
          </Box>
        )}
        {!loading && displayResult.length === 0 && !hasPersonalProfiles && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <p>{t('investments.markeet.realEstate.noSearchProfiles')}</p>
            <RealEstateSearchProfileForm
              trigger={
                <Button variant="outlined" size="small">
                  {t('investments.markeet.realEstate.createProfile')}
                </Button>
              }
              onCreated={handleProfileCreated}
              onError={() => console.error('[RealEstateCard] Erreur formulaire')}
            />
          </Box>
        )}
        {displayResult.map((item, index) => (
          <Box
            key={index}
            display="flex"
            sx={{
              p: 1,
              borderRadius: 1.5,
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.05)',
              transition: 'background 200ms ease',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            }}
          >
            <Box
              sx={{
                width: 110,
                height: 80,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                mr: 1.5,
                borderRadius: 1,
                backgroundColor: 'rgba(0,0,0,0.25)',
                flexShrink: 0,
              }}
            >
              <img src={item.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box display="flex" alignItems="baseline" gap={1}>
                <Box sx={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name}
                </Box>
                <Box sx={{ ml: 'auto', fontWeight: 700, color: '#cfe1ff' }}>
                  {parseFloat(item.price).toFixed(0)} €
                </Box>
                <AddOpportunityAction
                  initialValues={{
                    name: item.name,
                    price: item.price,
                    industry: item.localisation,
                    value_change_pct: item.rendement,
                    description: `${item.superficie_m2} m² · ${item.loyer_mensuel}€/mois`,
                  }}
                />
              </Box>
              <Box sx={{ fontSize: 11, color: invTheme.positive, fontWeight: 600 }}>
                {parseFloat(item.prix_m2)} €/m²
              </Box>
              <Box sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, mt: 0.25 }}>
                {item.superficie_m2} m² · {item.localisation}
              </Box>
              <Box display="flex" alignItems="center" sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', mt: 0.5 }}>
                <Box>
                  {t('investments.markeet.realEstate.annualRent')}:&nbsp;
                  <Box component="span" sx={{ color: 'white' }}>
                    {item.loyer_mensuel * 12} €
                  </Box>
                </Box>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ fontSize: 10, textTransform: 'uppercase' }}>
                    {t('investments.markeet.realEstate.grossYield')}
                  </Box>
                  <Delta value={item.rendement} unit="%" />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
